import type { SceneObject } from "../SceneHost";
import { applyPathGeometry, type PathPoint } from "../pathGeometry";
import { isSegmentObject } from "../segmentGeometry";
import type { ObjectTransformSnapshot } from "./resize";

export function objectRotationRad(obj: SceneObject): number {
  return typeof obj.rotation === "number" && Number.isFinite(obj.rotation) ? obj.rotation : 0;
}

export function rotatePoint(
  x: number,
  y: number,
  cx: number,
  cy: number,
  deltaRad: number
): [number, number] {
  const cos = Math.cos(deltaRad);
  const sin = Math.sin(deltaRad);
  const dx = x - cx;
  const dy = y - cy;
  return [cx + dx * cos - dy * sin, cy + dx * sin + dy * cos];
}

/**
 * Точка в системе координат объекта: отменяем его поворот вокруг центра.
 *
 * После этого попадание проверяется по обычному невёрнутому прямоугольнику —
 * точно, а не «по габариту». Без этого доска считала повёрнутую картинку
 * стоящей как прежде: взять её можно было там, где её уже нет.
 */
export function pointInObjectSpace(
  obj: SceneObject,
  x: number,
  y: number
): [number, number] {
  const rot = objectRotationRad(obj);
  if (Math.abs(rot) < 1e-6) return [x, y];
  const w = typeof obj.w === "number" ? obj.w : 0;
  const h = typeof obj.h === "number" ? obj.h : 0;
  return rotatePoint(x, y, obj.x + w / 2, obj.y + h / 2, -rot);
}

/** Прямоугольник по осям, в который целиком влезает повёрнутый объект. */
export function rotatedObjectBounds(obj: SceneObject): {
  x: number;
  y: number;
  w: number;
  h: number;
} {
  const w = typeof obj.w === "number" ? obj.w : 0;
  const h = typeof obj.h === "number" ? obj.h : 0;
  const rot = objectRotationRad(obj);
  if (Math.abs(rot) < 1e-6) return { x: obj.x, y: obj.y, w, h };
  const cx = obj.x + w / 2;
  const cy = obj.y + h / 2;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const [lx, ly] of [
    [obj.x, obj.y],
    [obj.x + w, obj.y],
    [obj.x + w, obj.y + h],
    [obj.x, obj.y + h],
  ] as [number, number][]) {
    const [px, py] = rotatePoint(lx, ly, cx, cy, rot);
    minX = Math.min(minX, px);
    minY = Math.min(minY, py);
    maxX = Math.max(maxX, px);
    maxY = Math.max(maxY, py);
  }
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

export function svgGroupTransform(obj: SceneObject): string {
  const w = typeof obj.w === "number" ? obj.w : 0;
  const h = typeof obj.h === "number" ? obj.h : 0;
  const rot = objectRotationRad(obj);
  if (Math.abs(rot) < 1e-6) {
    return `translate(${obj.x},${obj.y})`;
  }
  const deg = (rot * 180) / Math.PI;
  return `translate(${obj.x},${obj.y}) rotate(${deg}, ${w / 2}, ${h / 2})`;
}

import { usesRotationField } from "./rotationField";

export { usesRotationField };

export function pointerAngleRad(
  center: { x: number; y: number },
  pointer: { x: number; y: number }
): number {
  return Math.atan2(pointer.y - center.y, pointer.x - center.x);
}

export function applyGroupRotationDelta(
  obj: SceneObject,
  snap: ObjectTransformSnapshot,
  groupCenter: { x: number; y: number },
  deltaRad: number
): void {
  const w = snap.w;
  const h = snap.h;
  const objCx = snap.x + w / 2;
  const objCy = snap.y + h / 2;
  const [ncx, ncy] = rotatePoint(objCx, objCy, groupCenter.x, groupCenter.y, deltaRad);
  obj.x = Math.round(ncx - w / 2);
  obj.y = Math.round(ncy - h / 2);
  obj.w = w;
  obj.h = h;

  if (Array.isArray(snap.points) && (obj.type === "path" || isSegmentObject(obj))) {
    const nextPoints = snap.points.map(([px, py]) => {
      const [rx, ry] = rotatePoint(px, py, groupCenter.x, groupCenter.y, deltaRad);
      return [Math.round(rx), Math.round(ry)] as PathPoint;
    });
    obj.points = nextPoints;
    if (obj.type === "path") {
      applyPathGeometry(obj as SceneObject & { points: PathPoint[] }, nextPoints);
    }
    return;
  }

  if (usesRotationField(obj)) {
    obj.rotation = (snap.rotation ?? 0) + deltaRad;
  }
}

export function selectionShowsRotateHandle(objects: SceneObject[]): boolean {
  if (objects.length === 1 && isSegmentObject(objects[0]!)) return false;
  if (objects.length === 1 && objects[0]!.type === "audio") return false;
  if (objects.length === 1 && objects[0]!.type === "pdf_document") return false;
  if (objects.length === 1 && objects[0]!.type === "timer") return false;
  if (objects.length === 1 && objects[0]!.type === "calculator") return false;
  if (objects.length === 1 && objects[0]!.type === "frame") return false;
  return objects.length > 0;
}

export const ROTATE_HANDLE_OFFSET = 28;
